import { useEffect, useReducer } from 'react';

// Returns `value` until `ttlMs` after `updatedAt`, then null. A timer flips it
// at the exact expiry so the consumer re-renders without owning an effect — the
// TTL is a property of the value (e.g. a server-side session window), not of the
// render cycle. See .claude/rules/anti-patterns.md (#2 self-cancelling effect).
export const useExpiringValue = <T>(
  value: T | null | undefined,
  updatedAt: number,
  ttlMs: number,
): T | null => {
  const [, registerExpiry] = useReducer((tick: number) => tick + 1, 0);

  const hasValue = value !== null && value !== undefined;
  const expiresAt = updatedAt + ttlMs;

  useEffect(() => {
    if (!hasValue) {
      return;
    }

    const remaining = expiresAt - Date.now();
    if (remaining <= 0) {
      return;
    }

    const timeout = setTimeout(registerExpiry, remaining);
    return () => clearTimeout(timeout);
  }, [hasValue, expiresAt]);

  return hasValue && Date.now() < expiresAt ? value : null;
};
